import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea.jsx';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Thermometer, AlertTriangle, CheckCircle2, Info, AlertCircle, Plus, Loader2, X, Check, Clock, CalendarClock, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Datum-Helfer ──────────────────────────────────────────────────────────────
function getDateChips() {
  const chips = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().split('T')[0];
    const label = i === 0 ? 'Heute' : i === 1 ? 'Gestern' : d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    chips.push({ iso, label, isToday: i === 0 });
  }
  return chips;
}

export const TemperatureRecording = () => {
  const { user } = useAuth();
  const [measuringPoints, setMeasuringPoints] = useState([]);
  const [recentReadings, setRecentReadings] = useState([]);
  const [todayGrouped, setTodayGrouped] = useState({}); // Neu: Gruppiert nach Messpunkt
  const [todayReadings, setTodayReadings] = useState([]); // Für Rückwärtskompatibilität
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isAlarm, setIsAlarm] = useState(false);
  const [formData, setFormData] = useState({ temperature: '', comment: '' });
  const [inlineEntry, setInlineEntry] = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({}); // Welche Punkt-Historien sind offen

  // ── Rückwirkende Eingabe ──────────────────────────────────────────────────
  const dateChips = getDateChips();
  const [selectedDate, setSelectedDate] = useState(dateChips[0].iso); // heute
  const [selectedTime, setSelectedTime] = useState('');               // leer = aktuelle Zeit
  const [dateReadings, setDateReadings] = useState([]);               // Messungen für gewähltes Datum

  useEffect(() => {
    fetchData();
  }, []);

  // Wenn Datum wechselt → Messungen für dieses Datum laden
  const fetchDateReadings = useCallback(async (dateIso) => {
    const locationId = user.location_ids?.[0];
    if (!locationId) return;
    try {
      const res = await axios.get(`${API}/temperature/date-readings`, {
        params: { location_id: locationId, date: dateIso }
      });
      setDateReadings(res.data);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchDateReadings(selectedDate);
    // Zeit zurücksetzen bei Datumswechsel (nur bei Vergangenheit sinnvoll)
    if (selectedDate !== dateChips[0].iso) setSelectedTime('');
  }, [selectedDate, fetchDateReadings]);

  // Check if current temperature is an alarm
  useEffect(() => {
    if (selectedPoint && formData.temperature !== '') {
      const temp = parseFloat(formData.temperature);
      const minTemp = selectedPoint.target_temp - selectedPoint.tolerance;
      const maxTemp = selectedPoint.target_temp + selectedPoint.tolerance;
      setIsAlarm(temp < minTemp || temp > maxTemp);
    } else {
      setIsAlarm(false);
    }
  }, [selectedPoint, formData.temperature]);

  const fetchData = async () => {
    try {
      const locationId = user.location_ids && user.location_ids.length > 0 ? user.location_ids[0] : null;
      
      if (!locationId) {
        toast.error('Kein Standort zugeordnet');
        setLoading(false);
        return;
      }

      const [pointsRes, readingsRes, todayRes, groupedRes] = await Promise.all([
        axios.get(`${API}/temperature/measuring-points?location_id=${locationId}`),
        axios.get(`${API}/temperature?location_id=${locationId}`),
        axios.get(`${API}/temperature/today?location_id=${locationId}`),
        axios.get(`${API}/temperature/today-grouped?location_id=${locationId}`),
      ]);
      
      const sorted = pointsRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setMeasuringPoints(sorted);
      setRecentReadings(readingsRes.data.slice(0, 10));
      setTodayReadings(todayRes.data);
      setTodayGrouped(groupedRes.data || {});
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPoint) { toast.error('Bitte wählen Sie einen Messpunkt aus'); return; }
    if (isAlarm && (!formData.comment || formData.comment.trim() === '')) {
      toast.error('Bei Temperaturabweichung ist ein Kommentar PFLICHT!'); return;
    }
    const locationId = user.location_ids?.[0];
    const isToday = selectedDate === dateChips[0].iso;
    try {
      const payload = {
        location_id: locationId,
        measuring_point: selectedPoint.name,
        measuring_point_id: selectedPoint.id,
        temperature: parseFloat(formData.temperature),
        comment: formData.comment || null,
        recorded_by: user.id,
        ...(isToday ? {} : {
          recording_date: selectedDate,
          recording_time: selectedTime || undefined,
        }),
      };
      await axios.post(`${API}/temperature`, payload);
      const suffix = isToday ? '' : ` (Nachtrag ${selectedDate})`;
      toast.success(isAlarm ? `Temperatur (mit Alarm) gespeichert!${suffix}` : `Temperatur gespeichert!${suffix}`);
      setFormData({ temperature: '', comment: '' });
      setSelectedPoint(null);
      setIsAlarm(false);
      fetchData();
      fetchDateReadings(selectedDate);
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Speichern');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Gibt Gruppen-Info für einen Messpunkt zurück (heute)
  const getPointTodayData = (measuringPointId) => {
    const grouped = todayGrouped[measuringPointId];
    if (grouped) return grouped;
    // Fallback auf alten todayReadings (für Rückwärtskompatibilität und Vergangenheitsdaten)
    const readings = selectedDate === dateChips[0].iso ? todayReadings : dateReadings;
    const pointReadings = readings.filter(r => r.measuring_point_id === measuringPointId);
    if (!pointReadings.length) return null;
    const temps = pointReadings.map(r => r.temperature);
    return {
      latest: pointReadings[pointReadings.length - 1],
      all: pointReadings,
      daily_average: temps.length ? Math.round((temps.reduce((a, b) => a + b, 0) / temps.length) * 10) / 10 : null,
      count: pointReadings.length,
      has_alarm: pointReadings.some(r => r.is_alarm),
    };
  };

  // Legacy: Für Kompatibilität mit altem Code
  const isMeasuredOnDate = (measuringPointId) => {
    if (selectedDate === dateChips[0].iso) {
      return getPointTodayData(measuringPointId)?.latest || null;
    }
    return dateReadings.find(r => r.measuring_point_id === measuringPointId) || null;
  };

  const getTodayInfo = (measuringPointId) => {
    const data = getPointTodayData(measuringPointId);
    if (!data) return null;
    const reading = data.latest;
    const time = new Date(reading.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return { time, name: reading.recorded_by_name || 'Mitarbeiter', reading, count: data.count, average: data.daily_average, all: data.all, hasAlarm: data.has_alarm };
  };

  const isReadingInRange = (reading, point) => {
    if (!point) return true;
    const temp = reading.temperature;
    const minTemp = point.target_temp - point.tolerance;
    const maxTemp = point.target_temp + point.tolerance;
    return temp >= minTemp && temp <= maxTemp;
  };

  // Alarm-Check für Inline-Eingabe
  const getInlineAlarm = (point) => {
    if (!inlineEntry || inlineEntry.pointId !== point.id || inlineEntry.temp === '') return false;
    const temp = parseFloat(inlineEntry.temp);
    if (isNaN(temp)) return false;
    return temp < (point.target_temp - point.tolerance) || temp > (point.target_temp + point.tolerance);
  };

  // Inline-Speichern
  const handleInlineSave = async (point) => {
    const alarm = getInlineAlarm(point);
    if (alarm && !inlineEntry.comment.trim()) {
      toast.error('Bei Temperaturabweichung ist ein Kommentar PFLICHT!');
      return;
    }
    const locationId = user.location_ids?.[0];
    const isToday = selectedDate === dateChips[0].iso;
    setInlineSaving(true);
    try {
      await axios.post(`${API}/temperature`, {
        location_id: locationId,
        measuring_point: point.name,
        measuring_point_id: point.id,
        temperature: parseFloat(inlineEntry.temp),
        comment: inlineEntry.comment || null,
        recorded_by: user.id,
        ...(isToday ? {} : {
          recording_date: selectedDate,
          recording_time: selectedTime || undefined,
        }),
      });
      const suffix2 = isToday ? '' : ` (Nachtrag ${selectedDate})`;
      toast.success(alarm ? `Temperatur (mit Alarm) gespeichert!${suffix2}` : `Temperatur gespeichert!${suffix2}`);
      setInlineEntry(null);
      fetchData();
      fetchDateReadings(selectedDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setInlineSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="temperature-recording-title">
            Temperatur aufnehmen
          </h1>
          <p className="text-muted-foreground mt-2">Erfassen Sie die Temperaturen Ihrer Kühlgeräte</p>
        </div>

        {measuringPoints.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Thermometer className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Keine Messpunkte verfügbar</p>
              <p className="text-sm text-muted-foreground mt-2">Bitte wenden Sie sich an Ihren Administrator</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ── Datum-Auswahl (Rückwirkende Eingabe) ────────────────── */}
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarClock className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-sm font-medium">Messung für welchen Tag?</span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {dateChips.map(chip => (
                    <button
                      key={chip.iso}
                      onClick={() => setSelectedDate(chip.iso)}
                      data-testid={`date-chip-${chip.iso}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        selectedDate === chip.iso
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                      }`}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>

                {/* Uhrzeit nur wenn Vergangenheit gewählt */}
                {selectedDate !== dateChips[0].iso && (
                  <div className="mt-3 flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                    <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                        Nachtrag für {new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-amber-700 dark:text-amber-400">Uhrzeit der Messung:</label>
                        <input
                          type="time"
                          value={selectedTime}
                          onChange={e => setSelectedTime(e.target.value)}
                          className="h-7 px-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 text-xs"
                          data-testid="backdate-time-input"
                        />
                        <span className="text-xs text-amber-600">(leer = aktuelle Uhrzeit)</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status für gewähltes Datum */}
                {(() => {
                  // Unique gemessene Messpunkte (nicht Gesamt-Readings!)
                  const measuredIds = selectedDate === dateChips[0].iso
                    ? new Set(Object.keys(todayGrouped))
                    : new Set(dateReadings.map(r => r.measuring_point_id));
                  const done = measuredIds.size;
                  const total = measuringPoints.length;
                  const missing = total - done;
                  if (missing === 0 && total > 0) return (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Alle {total} Messpunkte für diesen Tag erfasst
                    </div>
                  );
                  if (missing > 0) return (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {missing} von {total} Messpunkt{missing !== 1 ? 'en' : ''} noch nicht erfasst
                    </div>
                  );
                  return null;
                })()}
              </CardContent>
            </Card>
            <Card className={isAlarm ? 'border-red-500 border-2' : ''}>
              <CardHeader>
                <CardTitle className={isAlarm ? 'text-red-600' : ''}>
                  {isAlarm ? '⚠️ ALARM - Temperaturabweichung!' : 'Neue Messung erfassen'}
                </CardTitle>
                <CardDescription>
                  Wählen Sie einen Messpunkt und geben Sie die Temperatur ein
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="measuring-point">Messpunkt auswählen *</Label>
                    <select
                      id="measuring-point"
                      value={selectedPoint?.id || ''}
                      onChange={(e) => {
                        const point = measuringPoints.find(p => p.id === e.target.value);
                        setSelectedPoint(point || null);
                        setFormData(prev => ({ ...prev, temperature: '' }));
                      }}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      required
                    >
                      <option value="">Bitte auswählen</option>
                      {measuringPoints.map(point => {
                        const measured = isMeasuredOnDate(point.id);
                        return (
                          <option key={point.id} value={point.id}>
                            {point.name} {measured ? '✓ (bereits erfasst)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {selectedPoint && (
                    <>
                      {/* Richtwerte-Anzeige */}
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                          <Thermometer className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                            <p className="font-semibold text-blue-900 dark:text-blue-200">{selectedPoint.name}</p>
                            <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">
                              <strong>Soll: {selectedPoint.target_temp}°C (±{selectedPoint.tolerance}°C)</strong>
                            </p>
                            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                              Erlaubter Bereich: {selectedPoint.target_temp - selectedPoint.tolerance}°C bis {selectedPoint.target_temp + selectedPoint.tolerance}°C
                            </p>
                            {selectedPoint.hint && (
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                                <Info className="h-3 w-3" /> {selectedPoint.hint}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bereits gemessen – zeige Info statt Blockierung */}
                      {(() => {
                        const todayInfo = getTodayInfo(selectedPoint.id);
                        if (todayInfo) {
                          const point = measuringPoints.find(p => p.id === selectedPoint.id);
                          const inRange = isReadingInRange(todayInfo.reading, point);
                          return (
                            <div className={`p-3 rounded-xl border ${inRange ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-300'}`}>
                              <div className="flex items-start gap-2">
                                {inRange
                                  ? <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                  : <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                                }
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-semibold ${inRange ? 'text-blue-800' : 'text-orange-800'}`}>
                                    {todayInfo.count === 1 ? '1 Messung heute' : `${todayInfo.count} Messungen heute`}
                                    {todayInfo.count > 1 && todayInfo.average !== null && (
                                      <span className="ml-2 font-bold">· Ø {todayInfo.average}°C</span>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Letzte: <strong>{todayInfo.reading.temperature}°C</strong> um {todayInfo.time}
                                    {!inRange && <span className="text-orange-600 ml-1">⚠️ Abweichung</span>}
                                  </p>
                                  <p className="text-xs text-blue-600 mt-1 font-medium">
                                    Neue Messung eintragen ist möglich (optional)
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="temperature">Temperatur (°C) *</Label>
                    <Input
                      id="temperature"
                      type="number"
                      step="0.1"
                      placeholder="z.B. -18.5"
                      value={formData.temperature}
                      onChange={(e) => handleChange('temperature', e.target.value)}
                      required
                      data-testid="temperature-input"
                      className={isAlarm ? 'border-red-500 border-2 bg-red-50' : ''}
                    />
                    {isAlarm && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>Temperatur liegt außerhalb des erlaubten Bereichs!</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comment" className={isAlarm ? 'text-red-600 font-semibold' : ''}>
                      Kommentar {isAlarm ? '(PFLICHT bei Abweichung!) *' : '(optional)'}
                    </Label>
                    <Textarea
                      id="comment"
                      placeholder={isAlarm ? 'Bitte erklären Sie die Abweichung...' : 'Optionaler Kommentar...'}
                      value={formData.comment}
                      onChange={(e) => handleChange('comment', e.target.value)}
                      required={isAlarm}
                      className={isAlarm && !formData.comment ? 'border-red-500 border-2' : ''}
                    />
                    {isAlarm && (
                      <p className="text-xs text-red-600">
                        Bei Temperaturabweichung müssen Sie einen Kommentar eingeben, um die Messung zu speichern.
                      </p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className={`w-full ${isAlarm ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    data-testid="submit-temperature-button"
                  >
                    {isAlarm 
                      ? 'Temperatur mit ALARM speichern'
                      : selectedPoint && isMeasuredOnDate(selectedPoint.id)
                        ? 'Weitere Messung speichern'
                        : 'Temperatur speichern'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Messungen für gewähltes Datum */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {selectedDate === dateChips[0].iso ? 'Heutige Messungen' : (
                    <>
                      <span className="text-amber-600">Nachtrag:</span>{' '}
                      {new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                    </>
                  )}
                </CardTitle>
                <CardDescription>
                  {selectedDate === dateChips[0].iso
                    ? 'Übersicht der heute erfassten Temperaturen'
                    : 'Bereits eingetragene Messungen für diesen Tag'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {measuringPoints.length > 0 ? (
                  <div className="space-y-3">
                    {measuringPoints.map(point => {
                      const todayInfo = getTodayInfo(point.id);
                      const inRange = todayInfo ? isReadingInRange(todayInfo.reading, point) : true;
                      const isInlineOpen = inlineEntry?.pointId === point.id; // Immer öffnbar
                      const inlineAlarm = isInlineOpen ? getInlineAlarm(point) : false;
                      const isHistoryOpen = !!expandedHistory[point.id];

                      return (
                        <div
                          key={point.id}
                          className={`rounded-xl border transition-all ${
                            todayInfo
                              ? inRange
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200'
                                : 'bg-red-50 dark:bg-red-900/20 border-red-300 border-2'
                              : isInlineOpen
                                ? inlineAlarm
                                  ? 'bg-red-50 border-red-300 border-2'
                                  : 'bg-amber-50 dark:bg-amber-900/10 border-amber-300 border-2'
                                : 'bg-gray-50 dark:bg-slate-700/40 border-gray-200 dark:border-slate-600 cursor-pointer hover:border-amber-400 hover:shadow-sm active:scale-[0.99]'
                          }`}
                          onClick={() => {
                            if (!isInlineOpen) {
                              setInlineEntry({ pointId: point.id, temp: '', comment: '', showComment: false });
                            }
                          }}
                          data-testid={`today-point-${point.id}`}
                        >
                          {/* Header-Zeile */}
                          <div className="flex items-center justify-between p-3">
                            <div className="flex items-center gap-3">
                              <Thermometer className={`h-5 w-5 shrink-0 ${
                                todayInfo
                                  ? inRange ? 'text-green-600' : 'text-red-600'
                                  : isInlineOpen ? 'text-amber-500' : 'text-gray-400'
                              }`} />
                              <div>
                                <p className="font-medium">{point.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  Soll: {point.target_temp}°C (±{point.tolerance}°C)
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {todayInfo ? (
                                <div className="text-right">
                                  <div className="flex items-center gap-1.5">
                                    <Badge variant="outline" className={inRange ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                      {todayInfo.reading.temperature}°C {!inRange && '⚠️'}
                                    </Badge>
                                    {/* Durchschnitt-Badge wenn mehrere Messungen */}
                                    {todayInfo.count > 1 && todayInfo.average !== null && (
                                      <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                        Ø {todayInfo.average}°C
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {todayInfo.count > 1 ? `${todayInfo.count}× gemessen` : `${todayInfo.time} Uhr`}
                                  </p>
                                </div>
                              ) : (
                                <Badge variant="outline" className={isInlineOpen ? 'bg-amber-100 text-amber-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {isInlineOpen ? 'Eingabe...' : 'Ausstehend'}
                                </Badge>
                              )}
                              {/* "Neue Messung" Button – bei Abweichung prominent */}
                              {todayInfo && !isInlineOpen && (
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); setInlineEntry({ pointId: point.id, temp: '', comment: '', showComment: false }); }}
                                  className={`text-xs px-2 py-1 rounded-lg border transition-colors shrink-0 ${
                                    !inRange
                                      ? 'bg-orange-100 text-orange-700 border-orange-400 hover:bg-orange-200 font-semibold'
                                      : 'bg-muted text-muted-foreground border-border hover:border-primary/40'
                                  }`}
                                  data-testid={`new-measurement-btn-${point.id}`}
                                >
                                  + Messen
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Tages-History (einklappbar) – nur wenn > 1 Messung */}
                          {todayInfo && todayInfo.count > 1 && !isInlineOpen && (
                            <div className="px-3 pb-1">
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setExpandedHistory(prev => ({...prev, [point.id]: !prev[point.id]})); }}
                                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <ChevronDown className={`h-3 w-3 transition-transform ${isHistoryOpen ? 'rotate-180' : ''}`} />
                                {isHistoryOpen ? 'History ausblenden' : `Alle ${todayInfo.count} Messungen anzeigen`}
                              </button>
                              {isHistoryOpen && (
                                <div className="mt-1.5 space-y-1 pb-2" onClick={e => e.stopPropagation()}>
                                  {todayInfo.all.map((r, idx) => {
                                    const t = new Date(r.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                                    const inR = r.temperature >= point.target_temp - point.tolerance && r.temperature <= point.target_temp + point.tolerance;
                                    return (
                                      <div key={r.id} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${inR ? 'bg-green-50/60' : 'bg-red-50/60'}`}>
                                        <span className="text-muted-foreground">{idx+1}. Messung · {t} Uhr</span>
                                        <span className={`font-semibold ${inR ? 'text-green-700' : 'text-red-700'}`}>
                                          {r.temperature}°C {!inR && '⚠️'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  <div className="flex items-center justify-between text-xs px-2 py-1 bg-blue-50/60 rounded font-semibold">
                                    <span className="text-blue-700">Tages-Durchschnitt</span>
                                    <span className="text-blue-800">Ø {todayInfo.average}°C</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Inline-Eingabeformular */}
                          {isInlineOpen && (
                            <div
                              className="px-3 pb-3 border-t border-amber-200 dark:border-amber-800 pt-3 space-y-2"
                              onClick={e => e.stopPropagation()}
                            >
                              {todayInfo && (
                                <p className="text-xs text-muted-foreground">
                                  {todayInfo.count}. Messung heute ·
                                  {todayInfo.count > 1 && ` Bisheriger Ø: ${todayInfo.average}°C ·`}
                                  Neue Messung:
                                </p>
                              )}
                              {inlineAlarm && (
                                <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium">
                                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                  Außerhalb des Toleranzbereichs! Kommentar ist PFLICHT.
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  placeholder="°C eingeben"
                                  value={inlineEntry.temp}
                                  onChange={e => setInlineEntry(prev => ({ ...prev, temp: e.target.value }))}
                                  className={`flex-1 h-10 text-base ${inlineAlarm ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
                                  autoFocus
                                  style={{ fontSize: '16px' }}
                                  onKeyDown={e => { if (e.key === 'Enter') handleInlineSave(point); }}
                                  data-testid={`inline-temp-${point.id}`}
                                />
                                {!inlineEntry.showComment && !inlineAlarm && (
                                  <button
                                    type="button"
                                    onClick={() => setInlineEntry(prev => ({ ...prev, showComment: true }))}
                                    className="h-10 w-10 flex items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-600 shrink-0"
                                    title="Kommentar hinzufügen"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                                <Button
                                  size="sm"
                                  className={`h-10 px-3 shrink-0 ${inlineAlarm ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                                  onClick={() => handleInlineSave(point)}
                                  disabled={!inlineEntry.temp || inlineSaving}
                                  data-testid={`inline-save-${point.id}`}
                                >
                                  {inlineSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => setInlineEntry(null)}
                                  className="h-10 w-10 flex items-center justify-center rounded-md border border-gray-300 text-gray-400 hover:text-gray-600 shrink-0"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              {(inlineEntry.showComment || inlineAlarm) && (
                                <Textarea
                                  placeholder={inlineAlarm ? 'Kommentar PFLICHT – Grund für Abweichung...' : 'Optionaler Kommentar...'}
                                  value={inlineEntry.comment}
                                  onChange={e => setInlineEntry(prev => ({ ...prev, comment: e.target.value }))}
                                  rows={2}
                                  className={`text-sm resize-none ${inlineAlarm && !inlineEntry.comment ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
                                  data-testid={`inline-comment-${point.id}`}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Keine Messpunkte konfiguriert</p>
                )}
              </CardContent>
            </Card>

            {/* Recent readings */}
            {recentReadings.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Letzte Messungen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {recentReadings.map((reading, index) => (
                      <div key={index} className={`flex items-center justify-between py-2 border-b last:border-0 ${reading.is_alarm ? 'bg-red-50 dark:bg-red-900/20 -mx-4 px-4 rounded' : ''}`}>
                        <div>
                          <p className="font-medium">{reading.measuring_point}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(reading.created_at).toLocaleString('de-DE')}
                          </p>
                          {reading.is_alarm && reading.comment && (
                            <p className="text-xs text-red-600 mt-1">💬 {reading.comment}</p>
                          )}
                        </div>
                        <Badge variant="outline" className={reading.is_alarm ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'}>
                          {reading.temperature}°C {reading.is_alarm && '⚠️'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};
